// Portal — Records Page (Live API data — signed SOAP notes only)
import { useState, useEffect } from 'react';
import { FileText, Download, Lock, Calendar, Stethoscope } from 'lucide-react';

interface SoapRecord {
    id: string;
    chiefComplaint: string;
    signedAt: string;
    doctor: { firstName: string; lastName: string; specialty: string | null; calendarColor: string };
    appointment: { serviceType: string; scheduledStart: string } | null;
}

import { API_BASE } from '../config/api';

export function RecordsPage() {
    const [records, setRecords] = useState<SoapRecord[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => { loadRecords(); }, []);

    async function loadRecords() {
        setLoading(true);
        try {
            // Get the first patient for demo context
            const ptRes = await fetch(`${API_BASE}/patients?limit=1`);
            const ptJson = await ptRes.json();
            const pt = ptJson.data?.[0];
            if (!pt) { setLoading(false); return; }

            const res = await fetch(`${API_BASE}/encounters?patientId=${pt.id}&status=signed&limit=30`);
            const json = await res.json();
            setRecords(json.data || []);
        } catch {
            console.warn('API unavailable');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="fade-in">
            <div className="portal-header">
                <h1>Mi Historia Clínica</h1>
                <p>{loading ? '...' : `${records.length} registros`}</p>
            </div>

            <div className="portal-content">
                {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="portal-card"><div className="skeleton" style={{ height: 70 }} /></div>
                    ))
                ) : records.length === 0 ? (
                    <div className="empty-state" style={{ textAlign: 'center', padding: 40 }}>
                        <FileText size={48} color="var(--color-text-muted)" />
                        <p style={{ color: 'var(--color-text-secondary)', marginTop: 12 }}>No hay registros médicos aún</p>
                    </div>
                ) : (
                    records.map(record => (
                        <div key={record.id} className="portal-card">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 15 }}>
                                        {record.chiefComplaint || 'Consulta médica'}
                                    </div>
                                    <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: record.doctor.calendarColor, display: 'inline-block' }} />
                                        Dr. {record.doctor.firstName} {record.doctor.lastName}
                                        {record.doctor.specialty && ` — ${record.doctor.specialty}`}
                                    </div>
                                </div>
                                <span className="portal-badge portal-badge-success" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                    <Lock size={10} /> Firmada
                                </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ fontSize: 13, color: 'var(--color-text-muted)', display: 'flex', gap: 12 }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <Calendar size={12} />
                                        {record.signedAt
                                            ? new Date(record.signedAt).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })
                                            : '—'}
                                    </span>
                                    {record.appointment && (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <Stethoscope size={12} />
                                            {record.appointment.serviceType?.replace(/-/g, ' ')}
                                        </span>
                                    )}
                                </div>
                                <button style={{
                                    display: 'flex', alignItems: 'center', gap: 4,
                                    padding: '6px 12px', fontSize: 13, fontWeight: 500,
                                    color: 'var(--color-primary)', background: 'var(--color-primary-50)',
                                    borderRadius: 'var(--radius-md)', cursor: 'pointer',
                                }}>
                                    <Download size={14} /> PDF
                                </button>
                            </div>
                        </div>
                    ))
                )}

                <div className="portal-card" style={{ textAlign: 'center', background: 'var(--color-primary-50)', border: '1px dashed var(--color-primary)', color: 'var(--color-primary-dark)' }}>
                    <Lock size={20} style={{ marginBottom: 8 }} />
                    <p style={{ fontSize: 13, fontWeight: 500 }}>
                        Solo puede ver notas firmadas por su médico. Los borradores no son visibles aquí.
                    </p>
                </div>
            </div>
        </div>
    );
}
